// Table-owned columns such as `__row_number__` live in the saved order but not in the definitions.
const isInternalColumn = (id: string) => id.startsWith('__');

/**
 * A saved column order with the columns added since it was saved slotted in after their
 * nearest preceding defined column, instead of piling up at the end. Ids that are no longer
 * defined are dropped. Returns the stored order untouched when there is none.
 */
export const mergeColumnOrder = (stored: string[] | undefined, definitionIds: string[]): string[] | undefined => {
  if (!stored?.length) return stored;
  const defined = new Set(definitionIds);
  const result = stored.filter((id) => defined.has(id) || isInternalColumn(id));

  definitionIds.forEach((id, index) => {
    if (result.includes(id)) return;
    const predecessor = definitionIds
      .slice(0, index)
      .reverse()
      .find((candidate) => result.includes(candidate));
    if (predecessor) {
      result.splice(result.indexOf(predecessor) + 1, 0, id);
      return;
    }
    const firstDefined = result.findIndex((candidate) => defined.has(candidate));
    result.splice(firstDefined === -1 ? result.length : firstDefined, 0, id);
  });

  return result;
};

/** A saved visibility map with each column it has never seen given its default visibility. */
export const mergeColumnVisibility = (
  stored: Record<string, boolean> | undefined,
  columns: { id: string; isVisible?: boolean }[],
  initial?: Record<string, boolean>
): Record<string, boolean> | undefined => {
  if (!stored) return stored;
  const missing = columns.filter((column) => !(column.id in stored));
  if (missing.length === 0) return stored;
  return {
    ...stored,
    ...Object.fromEntries(missing.map((column) => [column.id, initial?.[column.id] ?? column.isVisible ?? true])),
  };
};
